import { createModel } from '@rematch/core';
import {
  BUSINESS_TYPE_ENUM,
  GALLERY_RENDER_ENUM,
  GALLERY_SKU_ENUM,
  GallerySku,
  PreConfiguredSkuGallery,
  SkuGalleryLabelCount,
} from 'domains/gallerySkuDomain';
import gallerySkuService from 'services/gallerySkuService';
import { GalleryDesignSize } from 'utils/galleryDesignSize';
import { GallerySkuFilter } from 'utils/gallerySkuFilter';
import { isCanvaSession } from 'utils/canvaSession';
import { isBuyer } from 'utils/rbac';
import type { RootModel } from '.';
import { BaseState, baseReducers } from './baseReducers';

interface GalleryState extends BaseState {
  galleryLabels: SkuGalleryLabelCount[];
  companyLabels: SkuGalleryLabelCount[];
  selectedBusinessType: BUSINESS_TYPE_ENUM | null;
  businessTypeSkus: GallerySku[];
  startingJobConfig: boolean;
  preConfiguredSkus: PreConfiguredSkuGallery[];
  galleryRenderType: GALLERY_RENDER_ENUM | GALLERY_RENDER_ENUM.DEFAULT;
  galleryModalType: GALLERY_SKU_ENUM;
  /**
   * Whether this org has an SSO-buyer gallery set up at all. Undefined until the labels have
   * been read once.
   *
   * The SSO-buyer shop is a separate, hand-curated catalogue: an org that has never had one
   * configured returns an empty list, and every surface built on it -- the Shop menu, the
   * gallery page -- then renders blank even though the org's ordinary gallery is full of
   * products. `useSsoBuyerExperience` reads this so the shop falls back to the ordinary one
   * instead of showing the buyer nothing.
   */
  ssoBuyerGallery?: boolean;
}

export const gallery = createModel<RootModel>()({
  state: {
    galleryLabels: [],
    companyLabels: [],
    selectedBusinessType: null,
    businessTypeSkus: [],
    startingJobConfig: false,
    preConfiguredSkus: [],
    galleryRenderType: GALLERY_RENDER_ENUM.DEFAULT,
    galleryModalType: GALLERY_SKU_ENUM.SUMMARY,
    ssoBuyerGallery: undefined,
  } as GalleryState,
  reducers: {
    ...baseReducers<GalleryState>(),
  },
  effects: (dispatch) => {
    const { gallery } = dispatch;

    return {
      updateGalleryModalType(galleryModalType: GALLERY_SKU_ENUM) {
        gallery.setState({
          galleryModalType,
        });
      },
      async getGalleryLabels(payload, rootState): Promise<void> {
        const user = rootState.app.user;
        // A buyer arriving from Canva is an SSO buyer too, but the SSO-buyer label set is not
        // populated for them -- taking that branch leaves the shop with no categories at all and
        // every gallery page blank. See utils/canvaSession for why this is a session fact rather
        // than something read off the current URL.
        if (user && user.isSsoUser && isBuyer(user) && !isCanvaSession()) {
          const labels = await gallerySkuService.getSsoBuyerGalleryLabels();
          // An org with no SSO-buyer gallery configured answers with an empty list. Rendering
          // that is a shop with nothing in it, which is never the right answer when the org's
          // ordinary gallery has products -- so fall through and read that instead, and record
          // the fact so the surfaces built on the SSO-buyer label shape step aside too.
          gallery.setState({ ssoBuyerGallery: Boolean(labels && labels.length) });
          if (labels && labels.length) {
            // Transform SsoBuyerGalleryLabel[] to SkuGalleryLabelCount[]
            const skuGalleryLabels = labels.map((label) => {
              const children = labels.filter((l) => l.parentId === label.id);
              const dropDownLabels = children.map((l) => ({
                id: l.id,
                name: l.name,
                businessTypes: [],
                active: true,
                description: '',
                dropDownLabels: [],
                image: l.image,
                parentId: label.id,
                summary: '',
                version: 0,
                galleryLabels: [],
              }));

              return {
                skuCount: 0,
                skuLabel: {
                  id: label.id,
                  name: label.name,
                  image: label.image,
                  parentId: label.parentId || 0,
                  businessTypes: [],
                  active: true,
                  description: '',
                  dropDownLabels: dropDownLabels,
                  summary: '',
                  version: 0,
                  galleryLabels: [],
                  companyLabelId: label.companyLabelId,
                  galleryLabelId: label.galleryLabelId,
                  galleryType: label.galleryType,
                },
              };
            });

            gallery.setState({
              galleryLabels: skuGalleryLabels,
              companyLabels: [], // Or handle separately if needed
            });
            return;
          }
          // Empty: fall through to the ordinary labels below rather than returning with none.
        }
        const labels = await gallerySkuService.getGalleryLabels();
        gallery.setState({
          galleryLabels: labels.galleryLabels,
          companyLabels: labels.companyLabels,
        });
      },

      async getGallerySkusByLabel(payload: number, rootState) {
        const user = rootState.app.user;
        // Same rule as the labels above, and for the same reason: taking the SSO-buyer branch
        // for a buyer who is not on that catalogue -- a Canva visit, or an org that never had
        // one configured -- is what leaves a category page with no products in it.
        if (
          user &&
          user.isSsoUser &&
          isBuyer(user) &&
          !isCanvaSession() &&
          rootState.gallery.ssoBuyerGallery !== false
        ) {
          const skus = await gallerySkuService.getSsoBuyerGallerySkusByLabel(
            payload
          );
          return { content: skus || [] };
        }
        return gallerySkuService.getGallerySkusByLabel(payload);
      },

      async getCompanySkusByLabel(payload: {
        companyLabelId?: number;
        galleryLabelId?: number;
        page?: number;
        size?: number;
        filter?: GallerySkuFilter;
        designSize?: GalleryDesignSize;
        matchedOnly?: boolean;
      }) {
        return gallerySkuService.getCompanySkusByLabel(payload);
      },

      async getPreConfiguredSkus(
        payload: BUSINESS_TYPE_ENUM | undefined
      ): Promise<void> {
        let resp = await gallerySkuService.getPreConfiguredSkus(payload);
        gallery.setState({
          preConfiguredSkus: resp,
        });
      },

      setGalleryRenderType(payload: GALLERY_RENDER_ENUM): void {
        gallery.setState({
          galleryRenderType: payload,
        });
      },
    };
  },
});
